import { useReducedMotion } from 'motion/react';
import * as React from 'react';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';

import type { NavigationScreenState } from '@gitbook/app-navigation';
import type { RevisionPageDocument } from '@gitbook/hive-core';
import { findDOMNode } from '@gitbook/slate-react';
import { assertNever } from '@gitbook/util-ts';

import type { ScrollRef } from './PageSheetView';
import { useStorePageScrollPosition } from './PageSheetView/useStorePageScrollPosition';

/**
 * This hook decides where to scroll the page to, depending on:
 *  1. whether a comment is opened and we need to scroll to the comment node
 *  2. whether the user has navigated to a page with a hash in the URL (e.g. #section-1)
 *  3. whether a page has a stored scroll position to scroll to
 *     (e.g. when navigating back to a page after editing it)
 *  4. whether a page has no stored scroll position, in which case we scroll
 *     to the top of the page.
 */
export function useInitialScrollPage(props: {
    scrollRef: ScrollRef;
    page?: RevisionPageDocument;
    screen?: NavigationScreenState;
}) {
    const { page, screen, scrollRef } = props;
    const { pageScrollPosition } = useStorePageScrollPosition(scrollRef, page?.id);
    const prefersReducedMotion = useReducedMotion() || false;

    React.useEffect(() => {
        const scrollInstructions = screen
            ? getScrollInstructions(screen, prefersReducedMotion)
            : undefined;
        const { scrollType, scrollTarget, scrollBehavior } = scrollInstructions ?? {
            scrollType: 'position',
            scrollTarget: pageScrollPosition ?? {
                top: 0,
                left: 0,
            },
            scrollBehavior: 'instant',
        };
        switch (scrollType) {
            case 'element':
                scrollIntoViewIfNeeded(scrollTarget, {
                    behavior: scrollBehavior,
                    block: 'start',
                    inline: 'nearest',
                    scrollMode: 'if-needed',
                });
                break;
            case 'position':
                if (scrollRef?.current) {
                    scrollRef.current.scrollTo({
                        top: scrollTarget.top,
                        left: scrollTarget.left,
                        behavior: scrollBehavior,
                    });
                }
                break;
            default:
                assertNever(scrollType, 'Unexpected scroll type in useInitialScrollPage');
        }
    }, [screen, scrollRef, prefersReducedMotion, pageScrollPosition]);
}

type ScrollInstructions =
    | { scrollType: 'element'; scrollTarget: Element; scrollBehavior: ScrollBehavior }
    | {
          scrollType: 'position';
          scrollTarget: { top: number; left: number };
          scrollBehavior: ScrollBehavior;
      };

function getScrollInstructions(
    screen: NavigationScreenState,
    prefersReducedMotion: boolean
): ScrollInstructions | undefined {
    // If the screen has a selected comment node, we scroll to the node
    const nodeId = getNodeToScrollFromScreen(screen);
    if (nodeId) {
        const node = findDOMNode(nodeId);
        if (node) {
            return {
                scrollType: 'element',
                scrollBehavior: getScrollBehaviour(prefersReducedMotion),
                scrollTarget: node,
            };
        }
    }

    // If the screen has a hash, we scroll to the element with that hash as an id
    if (screen?.hash) {
        const sectionEl = window.document.getElementById(screen.hash);
        if (sectionEl) {
            return {
                scrollType: 'element',
                scrollBehavior: getScrollBehaviour(prefersReducedMotion),
                scrollTarget: sectionEl,
            };
        }
    }
}

/**
 * Get the node id to scroll to from the screen state
 */
function getNodeToScrollFromScreen(screen: NavigationScreenState) {
    if (screen.type !== 'space') {
        return;
    }

    // comments sidesheet
    if (
        screen.sideSheet?.type === 'comments' &&
        (screen.sideSheet.comment || screen.sideSheet.context === 'preview')
    ) {
        return screen.sideSheet.node;
    }

    // audits sidesheet
    if (screen.sideSheet?.type === 'audits') {
        return screen.sideSheet.node;
    }
}

/**
 * Get the scroll behaviour from environment.
 *
 * If the user prefers reduced motion or if the predictable UI mode is enabled,
 * we use 'instant' scrolling, otherwise we use 'smooth'.
 */
function getScrollBehaviour(prefersReducedMotion: boolean): ScrollBehavior {
    return process.env.GITBOOK_PREDICTABLE_UI === 'true' || prefersReducedMotion
        ? 'instant'
        : 'smooth';
}
